<template>
  <div class="public-view-page">
    <!-- Page Header -->
    <div class="public-view-page__page-header">
      <div class="public-view-page__header-content">
        <div class="public-view-page__header-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
          </svg>
        </div>
        <div>
          <h2 class="public-view-page__page-title">Route Cost Report</h2>
          <p class="public-view-page__page-description">Plan routes and calculate transportation costs</p>
        </div>
      </div>
    </div>

    <!-- Left Panel - Route Planning -->
    <div v-if="panelVisible" class="public-view-page__panel">
      <DirectionsPanel :require-email="!isAuthenticated" />
    </div>

    <!-- Floating Toggle Button (Top Right on Map) -->
    <button 
      @click="panelVisible = !panelVisible"
      class="public-view-page__toggle-button"
      :class="{ 'public-view-page__toggle-button--collapsed': !panelVisible }"
      :title="panelVisible ? 'Hide Panel' : 'Show Panel'"
    >
      <svg 
        v-if="panelVisible"
        class="public-view-page__toggle-icon"
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          d="M15 18l-6-6 6-6" 
          stroke="currentColor" 
          stroke-width="2" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        />
      </svg>
      <svg 
        v-else
        class="public-view-page__toggle-icon"
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          d="M9 18l6-6-6-6" 
          stroke="currentColor" 
          stroke-width="2" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <!-- Map Container -->
    <div class="public-view-page__map-container">
      <div id="public-view-map" ref="mapContainer" class="public-view-page__map"></div>
    </div>
  </div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";
import DirectionsPanel from '../cresiteiq2/DirectionsPanel.vue';
import { mapState } from 'vuex';

// Dark map styles from CRESiteIQ2
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#263c3f' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b9a76' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#38414e' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#212a37' }]
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b3' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#746855' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#1f2835' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#f3d19c' }]
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#2f3948' }]
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#17263c' }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#515c6d' }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#17263c' }]
  }
];

export default {
  name: 'PublicViewPage',

  components: {
    DirectionsPanel,
  },

  data() {
    return {
      userEmail: '',
      panelVisible: true, // Control panel visibility
      
      // Map data
      map: null,
      loader: null,
      isDarkMode: false,
      themeObserver: null,
      googleMapsCheckInterval: null,
      mapInitialized: false
    };
  },

  computed: {
    ...mapState(['reatlas']),
    
    isAuthenticated() {
      return window.user && window.user.id;
    }
  },

  mounted() {
    // Check if user already provided info (stored in session)
    const storedEmail = sessionStorage.getItem('public_view_email');
    const storedName = sessionStorage.getItem('public_view_name');
    const storedPhone = sessionStorage.getItem('public_view_phone');
    
    if (storedEmail) {
      this.userEmail = storedEmail;
      // Update the header component with the user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = storedEmail;
        if (storedName) {
          this.$refs.header.userName = storedName;
        }
        if (storedPhone) {
          this.$refs.header.userPhone = storedPhone;
        }
      }
    }

    // Initialize map immediately
    this.$nextTick(() => {
      this.detectDarkMode();
      this.observeThemeChanges();
      this.initGoogleMapsLoader();
    });

    // Listen for user info submission from TripCostCalculator
    this.$root.$on('email-submitted', (userInfo) => {
      // Handle both old format (string) and new format (object)
      if (typeof userInfo === 'string') {
        this.userEmail = userInfo;
        if (this.$refs.header) {
          this.$refs.header.userEmail = userInfo;
        }
      } else {
        this.userEmail = userInfo.email;
        if (this.$refs.header) {
          this.$refs.header.userEmail = userInfo.email;
          this.$refs.header.userName = userInfo.fullName;
          this.$refs.header.userPhone = userInfo.phone;
        }
      }
    });
  },

  beforeDestroy() {
    // Disconnect theme observer
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    
    // Clear the Google Maps check interval
    if (this.googleMapsCheckInterval) {
      clearInterval(this.googleMapsCheckInterval);
    }

    // Disable directions mode
    if (this.$store && this.$store.dispatch) {
      this.$store.dispatch('disableDirectionsMode');
    }

    // Remove event listener
    this.$root.$off('email-submitted');
  },

  methods: {
    /**
     * Initialize Google Maps Loader
     */
    initGoogleMapsLoader() {
      this.loader = new Loader({
        apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        version: "quarterly",
        libraries: ["places"]
      });

      this.waitForGoogleMaps();
    },

    /**
     * Wait for Google Maps API to load
     */
    waitForGoogleMaps() {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        this.initializeMap();
        return;
      }

      // Load Google Maps
      this.loader.load().then(() => {
        console.log('[PublicView] Google Maps API loaded successfully');
        this.initializeMap();
      }).catch((error) => {
        console.error('[PublicView] Error loading Google Maps:', error);
        if (this.$toast) {
          this.$toast.error('Failed to load Google Maps. Please refresh the page.');
        }
      });
    },

    /**
     * Initialize Google Maps
     */
    initializeMap() {
      if (!window.google || !window.google.maps) {
        console.error('Google Maps API not loaded');
        return;
      }

      // Prevent re-initialization
      if (this.mapInitialized) {
        console.log('[PublicView] Map already initialized, skipping');
        return;
      }

      const mapStyles = this.isDarkMode ? DARK_MAP_STYLES : [];
      console.log('[PublicView] Initializing map. Dark mode:', this.isDarkMode);

      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        zoom: 4,
        styles: mapStyles,
        mapTypeId: 'roadmap',
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        fullscreenControlOptions: { 
          position: google.maps.ControlPosition.RIGHT_BOTTOM 
        }
      });

      // Mark as initialized
      this.mapInitialized = true;

      // Set map in Vuex store for DirectionsPanel to use
      if (this.$store && this.$store.state.reatlas) {
        // Check if directions mode is already enabled
        const isDirectionsModeEnabled = this.$store.state.reatlas.directions?.mode;
        
        // Set the map reference
        this.$store.state.reatlas.map = this.map;
        
        // Only enable directions mode if not already enabled
        if (!isDirectionsModeEnabled) {
          console.log('[PublicView] Enabling directions mode');
          
          // Use setTimeout to ensure map is fully initialized
          setTimeout(() => {
            if (this.$store && this.$store.dispatch) {
              this.$store.dispatch('enableDirectionsMode');
            }
          }, 100);
        } else {
          console.log('[PublicView] Directions mode already enabled');
        }
      }

      console.log('[PublicView] Map initialized successfully');
    },

    /**
     * Detect if dark mode is active
     */
    detectDarkMode() {
      const htmlElement = document.documentElement;
      const bodyElement = document.body;
      
      this.isDarkMode = htmlElement.classList.contains('dark') || 
                        bodyElement.classList.contains('dark') ||
                        htmlElement.getAttribute('data-theme') === 'dark';
      
      console.log('[PublicView] Dark mode detected:', this.isDarkMode);
    },

    /**
     * Observe theme changes
     */
    observeThemeChanges() {
      const observer = new MutationObserver(() => {
        const wasDark = this.isDarkMode;
        this.detectDarkMode();
        
        // Update map theme if it changed
        if (wasDark !== this.isDarkMode && this.map) {
          console.log('[PublicView] Theme changed to:', this.isDarkMode ? 'dark' : 'light');
          this.updateMapTheme(this.isDarkMode);
        }
      });

      // Observe both html and body elements
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme']
      });

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });

      this.themeObserver = observer;
    },

    /**
     * Update map theme
     */
    updateMapTheme(isDark) {
      if (!this.map) return;

      this.map.setOptions({
        styles: isDark ? DARK_MAP_STYLES : []
      });
    }
  }
};
</script>
